<template>
  <ValidationObserver ref="observer" slim v-slot="{ invalid, handleSubmit }">
    <v-form v-if="!mfaAuthWithMethods || isCodeSent" @submit.prevent="handleSubmit(verifyCode)">
      <v-card :loading="loading" :class="{ 'no-shadow': isMFaView }">
        <v-card-title class="px-0">
          <v-toolbar v-if="!isMFaView" class="px-0" flat dense>
            <v-toolbar-title class="primary--text">{{ $t("headers.emailAuthentication") }}</v-toolbar-title>
            <v-spacer />
            <v-btn id="dismiss" icon @click="dismiss">
              <v-icon color="primary">$close</v-icon>
            </v-btn>
          </v-toolbar>
        </v-card-title>
        <v-card-text class="black--text">
          <p v-if="isMFaView" class="font-weight-bold mb-4 text-h5">{{ $t("headers.emailAuthentication") }}</p>
          <p class="mb-0 font-weight-bold">{{ $t("messages.emailVal", { mail: email }) }}</p>
          <p>{{ $t("messages.emailMfaCodeDesc") }}</p>
          <p v-if="isCodeSent && !canResendCode" class="text-center">
            {{ $t("messages.resendSec", { seconds: secondsRemaining }) }}
          </p>
          <p v-if="resendEmailCode">{{ $t("messages.emailNewCode") }}</p>
          <div class="text-center mb-4">
            <v-btn
              v-if="isCodeSent"
              type="button"
              color="primary"
              class="mx-auto"
              :disabled="!canResendCode"
              @click.stop.prevent="onResendClick"
              >{{ $t("captions.resend") }}
            </v-btn>
          </div>
          <v-text-field-with-validation v-model="authCode" :numberOnly="true" rules="required" label="enterCode" :showCustomRequiredError="true" placeholder="enterCode" maxlength="6" autocomplete="off" />
          <div class="d-flex justify-center">
            <error-label
              class="mb-2"
              v-model="showApiError"
              :message="errorMessage"
              ref="errorLabel"
            />
          </div>
        </v-card-text>
        <v-card-actions class="d-flex flex-column pb-8 justify-center align-center">
          <v-btn type="button" color="primary" :disabled="invalid || invalidCodeLength" @click="verifyCode">
            {{ $t("captions.verify") }}
          </v-btn>
          <v-btn v-if="mfaAuthWithMethods" class="my-6" color="primary" @click="goToAuthenticationWindow">
            {{ $t("captions.tryAnotherMethod") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <div class="text-center d-flex justify-center align-center error-container" v-else>
      <v-progress-circular v-if="loading" class="my-2 mt-10" indeterminate size="54"></v-progress-circular>
      <error-label
        class="mb-2 d-inline-block"
        v-model="showApiError"
        :message="errorMessage"
        :timeout="5000"
        ref="errorLabel"
      ></error-label>
    </div>
  </ValidationObserver>
</template>

<script lang="ts">
import Vue from "vue";
import { ValidationObserver } from "vee-validate";
import { AuthService, mfaTypeEnum } from "@/service";

import VTextFieldWithValidation from "../form/VTextFieldWithValidation.vue";
import ErrorLabel from "@/components/objects/ErrorLabel.vue";
import { parseApiError } from "@/plugins/helper";

import { RootStoreInterface } from "@/store/types";
import { createHelpers } from "vuex-typescript-interface";

const { mapGetters, mapMutations } = createHelpers<RootStoreInterface>();

const getters = mapGetters(["user", "getMfaUsedHit"]);
const mutations = mapMutations(["setMfaUsedHit"])

export default Vue.extend({
  components: {
    ValidationObserver,
    VTextFieldWithValidation,
    ErrorLabel,
  },
  data: () => ({
    loading: false,
    email: "",
    isCodeSent: false,
    authCode: "",
    canResendCode: false,
    secondsRemaining: 60,
    resendEmailCode: false,
    resendTimer: null as ReturnType<typeof setInterval> | null,
    showApiError: false,
    errorMessage: "",
    isNumberVerified: false,
  }),
  props: {
    isParentVisible: {
      type: Boolean,
    },
    hasMfaMethods: {
      type: Boolean,
      default: false,
    }
  },
  computed: {
    ...getters,
    isMFaView(): boolean {
      return this.$route.path === "/mfa" || this.$route.name === "mfaComponent";
    },
    mfaAuthWithMethods(): boolean {
      return this.hasMfaMethods && this.isMFaView;
    },
    invalidCodeLength(): boolean  {
      return this.authCode.length != 6;
    },
    isHitThroughLogin(): boolean {
      return this.getMfaUsedHit;
    }
  },
  methods: {
    ...mutations,
    dismiss() {
      this.$emit("dismiss");
    },
    showError(msg: string) {
      this.errorMessage = msg;
      this.showApiError = true;
    },
    async sendCode() {
      try {
        this.loading = true;

        (!this.isHitThroughLogin || !this.hasMfaMethods) ? await AuthService.getMfaRequestCode({mfaType: mfaTypeEnum.EMAIL}) : this.setMfaUsedHit(false);

        this.isCodeSent = true;

        this.startResendCountdown();
      } catch (e: any) {
        console.log("error sending EMAIL OTP:", e);

        this.showError(parseApiError(e as any));
      }
      this.loading = false;
    },
    async verifyCode() {
       if (this.invalidCodeLength) return;

      try {
        this.loading = true;
        const payload = {
          authCode: this.authCode,
          mfaType: mfaTypeEnum.EMAIL,
        };

        const res = await AuthService.getMfaVerify(payload);
        const result = String(res.result || "").trim().toLowerCase();
        if (
          result === "success" ||
          result === "tenantselectionrequired" ||
          result.startsWith("theme:")
        ) {
          this.$emit("mfa-verified", res.result);
          return;
        }
        this.showError("Invalid code entered. Please try again.");
      } catch (e: any) {
        const maxLimitReached = e.status == 401;
        const customError = (!this.isMFaView || maxLimitReached) ? parseApiError(e as any) : "Invalid code entered. Please try again.";
        this.showError(customError);
      } finally {
        this.loading = false;
      }
    },
    onResendClick() {
      this.resetInitValues();
      this.sendCode();
      this.resendEmailCode = true;
    },
    startResendCountdown() {
      this.secondsRemaining = 60;
      this.canResendCode = false;
      if (this.resendTimer) clearInterval(this.resendTimer);

      this.resendTimer = setInterval(() => {
        if (this.secondsRemaining > 0) {
          this.secondsRemaining--;
        } else {
          this.canResendCode = true;
          clearInterval(this.resendTimer!);
          this.resendTimer = null;
        }
      }, 1000);
    },
    resetInitValues() {
      this.isCodeSent = false;
      this.authCode = "";
      this.canResendCode = false;
      if (this.resendTimer) clearInterval(this.resendTimer);
      this.resendTimer = null;
      this.showApiError = false;
      this.errorMessage = "";
      this.isNumberVerified = false;

      this.$refs.observer?.reset();
    },
    goToAuthenticationWindow() {
      this.setMfaUsedHit(false);
      this.$eventBus.$emit("hideAppBarBtn");
    },
  },
  watch: {
    isParentVisible(newVal: boolean) {
      if (newVal) {
        this.resetInitValues();
      }
    },
  },
  mounted() {
    this.email = this.user?.email || "";
     if(this.isMFaView) this.sendCode();
  },
  beforeDestroy() {
    if (this.resendTimer) clearInterval(this.resendTimer);
  },
});
</script>
<style lang="scss" scoped>
.v-card.no-shadow {
  box-shadow: none;
}

.error-container {
  height: 400px;

  &:empty {
    height: 200px;
  }
}
</style>
